import { DomHelper } from '../modules/DomHelper';

/**
 * Modal
 *
 * HTML 構造:
 *   <button class="js-modal-trigger" data-modal="modal-id">開く</button>
 *
 *   <dialog id="modal-id" class="c-modal js-modal">
 *     <div class="c-modal__inner">
 *       <button class="js-modal-close c-modal__close" aria-label="閉じる">×</button>
 *       <div class="c-modal__content">...</div>
 *     </div>
 *   </dialog>
 */
export class Modal {
  private readonly dialog: HTMLDialogElement;
  private triggers: HTMLElement[] = [];

  constructor(dialog: HTMLDialogElement) {
    this.dialog = dialog;
    this.init();
  }

  private init(): void {
    const id = this.dialog.id;

    // トリガーボタンを収集
    this.triggers = DomHelper.qsa<HTMLElement>(`[data-modal="${id}"]`);
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });

    // 閉じるボタン
    DomHelper.qsa<HTMLElement>('.js-modal-close', this.dialog).forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // backdrop クリックで閉じる
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    // Escape キー（<dialog> のネイティブ動作を上書き）
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
  }

  open(): void {
    this.dialog.showModal();
    DomHelper.addClass(this.dialog, 'is-open');
    DomHelper.lockScroll();

    DomHelper.pushDataLayer({
      event: 'modal_open',
      modal_id: this.dialog.id,
    });
  }

  close(): void {
    DomHelper.removeClass(this.dialog, 'is-open');
    DomHelper.unlockScroll();

    // CSS トランジション完了後に dialog.close() を呼ぶ
    // transitionend が発火しない環境向けにタイムアウトをフォールバックとして設定
    let closed = false;
    const doClose = (): void => {
      if (closed) return;
      closed = true;
      this.dialog.close();
    };
    this.dialog.addEventListener('transitionend', doClose, { once: true });
    window.setTimeout(doClose, 350);
  }

  /** ページ内の全モーダルを初期化 */
  static initAll(selector = '.js-modal'): Modal[] {
    return DomHelper.qsa<HTMLDialogElement>(selector).map((el) => new Modal(el));
  }
}
